import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";

const COLORS = {
  ad: "#0F172A",
  phone: "#B91C1C",
  email: "#2563EB",
  username: "#9333EA",
  image: "#0F766E",
};

function layout(nodes, edges, width, height) {
  // Simple deterministic radial layout grouped by type
  const buckets = {};
  nodes.forEach((n) => {
    buckets[n.type] = buckets[n.type] || [];
    buckets[n.type].push(n);
  });
  const types = Object.keys(buckets);
  const centerX = width / 2;
  const centerY = height / 2;

  // central type ring: ad in center column, others in arcs around
  const positioned = {};
  const typeOrder = ["ad", "phone", "image", "username", "email"].filter((t) =>
    buckets[t]?.length,
  );
  typeOrder.forEach((type, ti) => {
    const list = buckets[type];
    const radius = 90 + ti * 110;
    list.forEach((n, i) => {
      const angle = (i / Math.max(1, list.length)) * Math.PI * 2;
      positioned[n.id] = {
        ...n,
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      };
    });
  });
  return positioned;
}

export default function GraphPage() {
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [data, setData] = useState({ nodes: [], edges: [] });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const params = focus ? { focus_id: focus } : {};
    api
      .get("/graph", { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [focus]);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(() => {
      const rect = wrapRef.current.getBoundingClientRect();
      setSize({ width: rect.width, height: Math.max(560, rect.height) });
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const positioned = layout(data.nodes, data.edges, size.width, size.height);
  const selectedNode = selected ? positioned[selected] : null;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="graph-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
          Relationship graph
        </div>
        <h1 className="font-display font-bold tracking-tight text-3xl">
          Entity relationships
        </h1>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          Ads link to the phones, emails, usernames and images they contain.
          Click a node to inspect or follow it. {focus && "Focused on a single ad."}
        </p>
      </header>

      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        <div
          ref={wrapRef}
          className="border border-slate-200 bg-white rounded-md overflow-hidden h-[480px] sm:h-[640px] relative"
        >
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading graph…</div>
          ) : (
            <svg width={size.width} height={size.height}>
              {data.edges.map((e, idx) => {
                const s = positioned[e.source];
                const t = positioned[e.target];
                if (!s || !t) return null;
                const isHighlighted =
                  selected && (selected === e.source || selected === e.target);
                return (
                  <line
                    key={`${e.source}->${e.target}`}
                    x1={s.x}
                    y1={s.y}
                    x2={t.x}
                    y2={t.y}
                    stroke={isHighlighted ? "#2563EB" : "#CBD5E1"}
                    strokeWidth={isHighlighted ? 1.5 : 1}
                  />
                );
              })}
              {Object.values(positioned).map((n) => {
                const isSelected = selected === n.id;
                return (
                  <g
                    key={n.id}
                    transform={`translate(${n.x}, ${n.y})`}
                    onClick={() => setSelected(n.id)}
                    style={{ cursor: "pointer" }}
                    data-testid={`node-${n.type}`}
                  >
                    <circle
                      r={n.type === "ad" ? 10 : 7}
                      fill={COLORS[n.type]}
                      stroke={isSelected ? "#2563EB" : "white"}
                      strokeWidth={isSelected ? 3 : 2}
                    />
                    {(n.type === "ad" || isSelected) && (
                      <text
                        x={12}
                        y={4}
                        fontSize="10"
                        fontFamily="IBM Plex Mono, monospace"
                        fill="#0F172A"
                      >
                        {n.label.slice(0, 24)}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}

          <div className="absolute top-3 left-3 bg-white/90 border border-slate-200 rounded-md px-3 py-2 text-xs space-y-1">
            {Object.entries(COLORS).map(([k, c]) => (
              <div key={k} className="flex items-center gap-2">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: c }}
                />
                <span className="capitalize">{k}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="border border-slate-200 bg-white rounded-md p-4 space-y-3 h-fit">
          <h2 className="font-display font-bold text-lg tracking-tight">
            Inspector
          </h2>
          {!selectedNode && (
            <p className="text-sm text-slate-500">
              Click any node in the graph to see details.
            </p>
          )}
          {selectedNode && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">
                  Type
                </div>
                <div className="capitalize">{selectedNode.type}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">
                  Label
                </div>
                <div className="font-mono text-xs break-all">{selectedNode.label}</div>
              </div>
              {selectedNode.type === "ad" && (
                <Link
                  to={`/ads/${selectedNode.id.replace("ad:", "")}`}
                  data-testid="inspector-open-ad"
                  className="inline-block text-sm bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-md"
                >
                  Open advertisement
                </Link>
              )}
              {selectedNode.type === "phone" && (
                <Link
                  to={`/lookup/phone?q=${encodeURIComponent(selectedNode.label)}`}
                  data-testid="inspector-phone-lookup"
                  className="inline-block text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md"
                >
                  Lookup phone
                </Link>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-slate-100 text-xs text-slate-500">
            <div className="flex justify-between">
              <span>Nodes</span>
              <span className="font-mono">{data.nodes.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Edges</span>
              <span className="font-mono">{data.edges.length}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
